import DOMPurify from "dompurify";

export class SanitizeUtils {
  /**
   * Sanitiza HTML para contenido de emails (permite más tags)
   */
  static sanitizeEmailHtml(html: string): string {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "b",
        "i",
        "span",
        "div",
        "a",
        "ul",
        "ol",
        "li",
        "table",
        "thead",
        "tbody",
        "tr",
        "td",
        "th",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
        "img"
      ],
      ALLOWED_ATTR: [
        "href",
        "title",
        "class",
        "style",
        "target",
        "colspan",
        "rowspan",
        "align",
        "src",
        "alt"
      ],
      ALLOW_DATA_ATTR: false,
      KEEP_CONTENT: true,
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      FORBID_ATTR: [
        "onerror",
        "onload",
        "onclick",
        "onmouseover",
        "onfocus",
        "onblur",
        "onsubmit",
        "onchange"
      ]
    });
  }

  /**
   * Sanitiza HTML para comentarios simples
   */
  static sanitizeCommentHtml(html: string): string {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "b", "i", "span", "div"],
      ALLOWED_ATTR: ["class"],
      ALLOW_DATA_ATTR: false,
      KEEP_CONTENT: true,
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      FORBID_ATTR: [
        "onerror",
        "onload",
        "onclick",
        "onmouseover",
        "onfocus",
        "onblur",
        "onsubmit",
        "onchange"
      ]
    });
  }

  /**
   * Sanitiza HTML básico (solo texto formateado)
   */
  static sanitizeBasicHtml(html: string): string {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "b", "i", "span"],
      ALLOWED_ATTR: ["class"],
      ALLOW_DATA_ATTR: false,
      KEEP_CONTENT: true,
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      FORBID_ATTR: [
        "onerror",
        "onload",
        "onclick",
        "onmouseover",
        "onfocus",
        "onblur",
        "onsubmit",
        "onchange"
      ]
    });
  }

  /**
   * Convierte HTML no confiable a texto plano sin ejecutar nada.
   * DOMParser produce un documento inerte: no corre scripts ni carga recursos
   * (a diferencia de `div.innerHTML`, donde `<img onerror>` si dispara).
   */
  static htmlToPlainText(html: string): string {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    // El codigo de un <script> o <style> no es texto visible: sin esto se
    // colaria en la linea compacta de la actividad.
    doc.body
      .querySelectorAll("script, style, noscript, template")
      .forEach((el) => el.remove());
    return doc.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
  }

  /**
   * Sanitiza texto plano (escape HTML)
   */
  static escapeHtml(text: string): string {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Sanitiza HTML con configuración personalizada
   */
  static sanitizeCustom(html: string, config: DOMPurify.Config): string {
    if (!html) return "";
    return DOMPurify.sanitize(html, config);
  }
}
